import React, { useEffect, useMemo, useState } from "react";
import { FileBarChart2, AlertTriangle, Loader2, ExternalLink, LayoutGrid, CalendarRange, FileText } from "lucide-react";
import {
  loadReportStatements, loadStatement, loadValueProvenance, loadNote, loadDocumentPage, groupStatement, valueDisplay,
} from "./ari";
import { Panel, SectionHead, StatusBadge, Field, Drawer, useSelection } from "./shared";

/* ---------------- shared governed states ---------------- */
function GovernedUnavailable({ error, what }) {
  return (
    <Panel className="flex flex-col items-center justify-center border-rose-200 bg-rose-50/50 py-14 text-center" data-testid="governed-unavailable">
      <AlertTriangle className="mb-3 h-9 w-9 text-rose-500" />
      <div className="text-lg font-extrabold text-rose-700">Governed source unavailable</div>
      <p className="mt-2 max-w-md text-sm text-rose-600/90">
        {what} could not be loaded from the governed Common API. The studio fails closed — no static financial fallback is shown.
      </p>
      {error && <div className="mt-3 rounded bg-white px-3 py-1 font-mono text-xs text-rose-500">{String(error)}</div>}
    </Panel>
  );
}
const Loading = ({ label }) => (
  <div className="flex items-center justify-center gap-2 py-14 text-sm text-slate-500" data-testid="governed-loading">
    <Loader2 className="h-4 w-4 animate-spin" />{label}
  </div>
);

/* ---------------- statement-type ↔ tab matching (UI routing only, governed types) ---------------- */
const normType = (s) => String(s || "").toUpperCase().replace(/[^A-Z]/g, "");
const STATEMENT_TABS = [
  { key: "overview", label: "Overview" },
  { key: "balance_sheet", label: "Balance Sheet", match: (t) => t.includes("BALANCESHEET") },
  { key: "profit_loss", label: "Profit & Loss", match: (t) => t.includes("PROFITANDLOSS") || t.includes("PROFITLOSS") || (t.includes("PROFIT") && t.includes("LOSS")) },
  { key: "cash_flow", label: "Cash Flow", match: (t) => t.includes("CASHFLOW") },
  { key: "changes_in_equity", label: "Changes in Equity", match: (t) => t.includes("CHANGESINEQUITY") || (t.includes("EQUITY") && t.includes("CHANGE")) },
  { key: "notes", label: "Notes & Schedules" },
];
function matchStatement(statements, basis, tabKey) {
  const tab = STATEMENT_TABS.find((t) => t.key === tabKey);
  if (!tab || !tab.match) return null;
  return (statements || []).find((s) => String(s.statement_role || "").toUpperCase() === String(basis).toUpperCase() && tab.match(normType(s.statement_type))) || null;
}

/* ---------------- governed value cell (§6, no inference) ---------------- */
function ValueCell({ value, isTotal, onDrill }) {
  const d = valueDisplay(value);
  if (d.render) {
    return (
      <button data-testid="fs-value" onClick={() => onDrill(value)}
        className={`w-full text-right font-mono tabular-nums hover:text-indigo-600 ${isTotal ? "font-bold text-slate-900" : "text-slate-700"}`}>
        {d.governed}
      </button>
    );
  }
  return <span data-testid="fs-value-state" title={d.semantics} className="text-xs italic text-slate-400">{d.token}</span>;
}

/* ---------------- provenance + note drawers (contextual evidence) ---------------- */
function useEvidenceDrawers() {
  const [drill, setDrill] = useState(null);
  const [note, setNote] = useState(null);
  const openDrill = (value) => {
    setDrill({ value, provenance: null, loading: true, error: null });
    loadValueProvenance(value.value_id)
      .then((p) => setDrill((d) => d && { ...d, provenance: p, loading: false }))
      .catch((e) => setDrill((d) => d && { ...d, error: e?.message || String(e), loading: false }));
  };
  const openNote = (ref) => {
    setNote({ ref, data: null, loading: true, error: null });
    loadNote(ref).then((n) => setNote((s) => s && { ...s, data: n, loading: false }))
      .catch((e) => setNote((s) => s && { ...s, error: e?.message || String(e), loading: false }));
  };
  const drawers = (
    <>
      <Drawer open={Boolean(drill)} onClose={() => setDrill(null)} title="Value provenance">
        {drill && (
          <div className="space-y-4 text-sm" data-testid="fs-provenance">
            <div className="rounded-lg bg-slate-50 p-3">
              <div className="font-bold text-slate-800">Value <span className="font-mono">{drill.value.value_id}</span></div>
              <dl className="mt-2 space-y-1 text-xs">
                <div className="flex justify-between"><dt className="text-slate-500">Raw value (source)</dt><dd className="font-mono text-slate-800">{String(drill.value.raw_value_exact ?? "—")}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Numeric (governed)</dt><dd className="font-mono text-slate-800">{String(drill.value.numeric_value ?? "—")}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Value semantics</dt><dd><StatusBadge value={drill.value.value_semantics} /></dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Review status</dt><dd><StatusBadge value={drill.value.review_status} /></dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Currency · scale</dt><dd className="text-slate-700">{String(drill.value.currency ?? "—")} · {String(drill.value.scale ?? "—")}</dd></div>
              </dl>
            </div>
            {drill.loading && <Loading label="Loading provenance…" />}
            {drill.error && <GovernedUnavailable error={drill.error} what="Provenance" />}
            {drill.provenance && (
              <div>
                <div className="mb-1 flex items-center gap-2 font-bold text-slate-700">Provenance <StatusBadge value={drill.provenance.provenance_state} /></div>
                <div className="mb-2 text-xs text-slate-500">page {drill.provenance.value?.page_number ?? drill.provenance.value?.zero_based_page_index ?? "—"} · document {drill.provenance.value?.document_id ?? "—"}</div>
                <div className="mb-1 font-bold text-slate-700">Source atoms ({(drill.provenance.sources || []).length})</div>
                <div className="max-h-72 overflow-auto rounded border border-slate-200">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50"><tr><th className="px-2 py-1 text-left">Fragment</th><th className="px-2 py-1">atom</th><th className="px-2 py-1">x0</th><th className="px-2 py-1">y0</th></tr></thead>
                    <tbody>
                      {(drill.provenance.sources || []).map((s) => (
                        <tr key={s.value_source_id} className="border-t border-slate-100">
                          <td className="px-2 py-1 font-mono">{s.source_fragment ?? s.raw_text}</td>
                          <td className="px-2 py-1 text-center">{s.atom_id}</td>
                          <td className="px-2 py-1 text-center">{s.x0}</td>
                          <td className="px-2 py-1 text-center">{s.y0}</td>
                        </tr>
                      ))}
                      {(drill.provenance.sources || []).length === 0 && <tr><td colSpan={4} className="px-2 py-3 text-center text-slate-400">No atom sources.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </Drawer>
      <Drawer open={Boolean(note)} onClose={() => setNote(null)} title={`Note ${note?.ref ?? ""}`}>
        {note && (
          <div className="space-y-3 text-sm" data-testid="fs-note">
            {note.loading && <Loading label="Loading governed note…" />}
            {note.error && <GovernedUnavailable error={note.error} what="This note" />}
            {note.data && (Array.isArray(note.data) ? note.data : [note.data]).map((n) => (
              <div key={n.id} className="rounded-lg bg-slate-50 p-3">
                <div className="mb-1 flex items-center gap-2 text-xs text-slate-500"><StatusBadge value={n.status} /><StatusBadge value={n.resolution_status} /><span>page {n.source_page_id}</span></div>
                <div className="whitespace-pre-wrap font-mono text-xs text-slate-700">{n.source_text}</div>
              </div>
            ))}
          </div>
        )}
      </Drawer>
    </>
  );
  return { openDrill, openNote, drawers };
}

/* ---------------- one governed statement (reusable) ---------------- */
export function GovernedStatementView({ statementId, basis, openDrill, openNote }) {
  const [grouped, setGrouped] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (statementId == null) { setGrouped(null); return; }
    setLoading(true); setErr(null); setGrouped(null);
    loadStatement(statementId, basis).then((p) => setGrouped(groupStatement(p)))
      .catch((e) => setErr(e?.message || String(e))).finally(() => setLoading(false));
  }, [statementId, basis]);

  if (err) return <GovernedUnavailable error={err} what="This statement" />;
  if (loading || !grouped) return <Loading label="Loading governed statement…" />;
  return (
    <Panel>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
        <div className="flex items-center gap-3 text-xs">
          <span className="rounded-md bg-indigo-600 px-2 py-1 text-[11px] font-bold text-white">{grouped.statement.statement_role || basis}</span>
          <span className="text-slate-600"><b data-testid="fs-row-count">{grouped.counts.rows}</b> rows · <b data-testid="fs-value-count">{grouped.counts.values}</b> values</span>
          <span className="text-slate-500">Unit: <b className="text-slate-700">{grouped.statement.unit || "—"}</b></span>
        </div>
        <div className="text-xs text-slate-500">Statement <span className="font-mono">{grouped.statement.statement_id}</span> · pages {grouped.statement.page_start ?? "—"}–{grouped.statement.page_end ?? "—"}</div>
      </div>
      <div className="overflow-auto rounded-lg border border-slate-200">
        <table className="w-full border-collapse text-sm" data-testid="financial-statement-table">
          <thead className="bg-slate-50">
            <tr>
              <th className="sticky left-0 z-10 border-b bg-slate-50 px-3 py-2 text-left font-bold text-slate-700">Line item</th>
              {grouped.columns.map((c) => (
                <th key={c.column_id} className="border-b px-3 py-2 text-right font-bold text-slate-700">
                  {c.heading || c.period_role || `col ${c.column_id}`}
                  <div className="font-normal text-[10px] text-slate-400">{c.period_role}{c.period_end ? ` · ${c.period_end}` : ""}</div>
                </th>
              ))}
              <th className="border-b px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {grouped.rows.map((r) => {
              const isTotal = ["TOTAL", "SUBTOTAL"].includes(String(r.row_type || "").toUpperCase());
              const isHeader = String(r.row_type || "").toUpperCase() === "HEADER";
              const indent = Number(r.indentation_level) || 0;
              return (
                <tr key={r.row_id} data-testid="fs-row" className="border-b border-slate-100 hover:bg-indigo-50/40">
                  <td className={`sticky left-0 z-10 bg-white px-3 py-2 ${isTotal ? "font-extrabold text-slate-900" : isHeader ? "font-bold text-slate-800" : "font-medium text-slate-700"}`} style={{ paddingLeft: 12 + indent * 16 }}>
                    {r.label || r.source_row_text || <span className="italic text-slate-400">unlabelled row</span>}
                    {r.note_reference && (
                      <button data-testid="fs-note-ref" onClick={() => openNote(r.note_reference)} className="ml-2 rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-bold text-indigo-700 hover:bg-indigo-100">note {r.note_reference}</button>
                    )}
                  </td>
                  {grouped.columns.map((c) => (
                    <td key={c.column_id} className="px-3 py-2 text-right"><ValueCell value={grouped.valueByCell.get(`${r.row_id}::${c.column_id}`)} isTotal={isTotal} onDrill={openDrill} /></td>
                  ))}
                  <td className="px-3 py-2 text-right text-[10px] font-mono text-slate-400">r{r.row_id}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/* ---------------- 5-year governed view (exact-label alignment, no fuzzy match) ---------------- */
function FiveYearView({ companyReports, tabKey, basis, openDrill }) {
  const [cols, setCols] = useState(null); // [{report, year, statementId, grouped}]
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true); setErr(null); setCols(null);
    const years = [...companyReports].sort((a, b) => String(b.financial_year).localeCompare(String(a.financial_year))).slice(0, 5);
    Promise.all(years.map(async (rep) => {
      const stmts = await loadReportStatements(rep.report_id);
      const match = matchStatement(stmts, basis, tabKey);
      if (!match) return { report: rep, year: rep.financial_year, grouped: null };
      const g = groupStatement(await loadStatement(match.statement_id, basis));
      return { report: rep, year: rep.financial_year, statementId: match.statement_id, grouped: g };
    })).then(setCols).catch((e) => setErr(e?.message || String(e))).finally(() => setLoading(false));
  }, [companyReports, tabKey, basis]);

  if (err) return <GovernedUnavailable error={err} what="The five-year comparison" />;
  if (loading || !cols) return <Loading label="Loading governed multi-year statements…" />;

  // exact governed label union, order from most-recent year
  const order = [];
  const seen = new Set();
  cols.forEach((c) => (c.grouped?.rows || []).forEach((r) => { const l = r.label || r.source_row_text; if (l && !seen.has(l)) { seen.add(l); order.push({ label: l, row_type: r.row_type }); } }));
  const yearValue = (col, label) => {
    const g = col.grouped; if (!g) return null;
    const row = g.rows.find((r) => (r.label || r.source_row_text) === label); if (!row) return null;
    const cur = g.columns.find((c) => String(c.period_role).toUpperCase() === "CURRENT") || g.columns[0];
    return cur ? g.valueByCell.get(`${row.row_id}::${cur.column_id}`) : null;
  };

  return (
    <Panel data-testid="five-year-view">
      <div className="mb-3 flex items-center gap-2 rounded-lg bg-amber-50/60 px-3 py-2 text-xs text-amber-800">
        <CalendarRange className="h-4 w-4" /> Aligned by <b>exact governed line-item label</b> across {cols.length} governed year(s). Rows present in only some years show a blank for missing years — no fuzzy matching or interpolation.
      </div>
      <div className="overflow-auto rounded-lg border border-slate-200">
        <table className="w-full border-collapse text-sm" data-testid="five-year-table">
          <thead className="bg-slate-50">
            <tr>
              <th className="sticky left-0 z-10 border-b bg-slate-50 px-3 py-2 text-left font-bold text-slate-700">Line item</th>
              {cols.map((c) => <th key={c.report.report_id} className="border-b px-3 py-2 text-right font-bold text-slate-700">{c.year}<div className="font-normal text-[10px] text-slate-400">report {c.report.report_id}</div></th>)}
            </tr>
          </thead>
          <tbody>
            {order.map((o) => {
              const isTotal = ["TOTAL", "SUBTOTAL"].includes(String(o.row_type || "").toUpperCase());
              return (
                <tr key={o.label} data-testid="fy-row" className="border-b border-slate-100 hover:bg-indigo-50/40">
                  <td className={`sticky left-0 z-10 bg-white px-3 py-2 ${isTotal ? "font-extrabold text-slate-900" : "font-medium text-slate-700"}`}>{o.label}</td>
                  {cols.map((c) => <td key={c.report.report_id} className="px-3 py-2 text-right"><ValueCell value={yearValue(c, o.label)} isTotal={isTotal} onDrill={openDrill} /></td>)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/* ---------------- workspace overview (governed) ---------------- */
function WorkspaceOverview({ report, statements, basis, setTab, openDrill }) {
  const [facts, setFacts] = useState(null);
  const bs = matchStatement(statements, basis, "balance_sheet");
  useEffect(() => {
    if (!bs) { setFacts([]); return; }
    let alive = true; setFacts(null);
    loadStatement(bs.statement_id, basis).then((p) => {
      if (!alive) return;
      const g = groupStatement(p);
      const col = g.columns.find((c) => String(c.period_role).toUpperCase() === "CURRENT") || g.columns[0];
      const out = [];
      for (const r of g.rows) {
        const v = col && g.valueByCell.get(`${r.row_id}::${col.column_id}`);
        const d = valueDisplay(v);
        if (d.render && ["TOTAL", "SUBTOTAL"].includes(String(r.row_type || "").toUpperCase())) out.push({ id: r.row_id, label: r.label, value: v });
        if (out.length >= 6) break;
      }
      setFacts(out);
    }).catch(() => setFacts([]));
    return () => { alive = false; };
  }, [bs, basis]);

  return (
    <div className="space-y-4" data-testid="workspace-overview">
      <div className="grid gap-4 md:grid-cols-3">
        <Panel><div className="text-[11px] font-bold uppercase text-slate-400">Report</div><div className="mt-1 text-lg font-extrabold text-slate-900">{report?.financial_year}</div><div className="text-xs text-slate-500">report <span className="font-mono">{report?.report_id}</span> · {report?.report_type}</div></Panel>
        <Panel><div className="text-[11px] font-bold uppercase text-slate-400">Governed statements</div><div className="mt-1 text-2xl font-extrabold text-slate-900">{(statements || []).length}</div><div className="text-xs text-slate-500">Source <b className="text-emerald-600">GOVERNED_COMMON_API</b></div></Panel>
        <Panel><div className="text-[11px] font-bold uppercase text-slate-400">Processing</div><div className="mt-1 text-sm font-bold text-slate-800"><StatusBadge value={report?.processing_status} /></div>{report?.official_source_url && <a href={report.official_source_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline">Annual report <ExternalLink className="h-3 w-3" /></a>}</Panel>
      </div>
      <Panel>
        <div className="mb-2 flex items-center justify-between"><div className="font-extrabold text-slate-800">Key governed figures (Balance Sheet)</div><button onClick={() => setTab("balance_sheet")} className="text-xs font-semibold text-indigo-600 hover:underline">Open Balance Sheet →</button></div>
        {facts == null ? <Loading label="Loading key figures…" /> : facts.length ? (
          <table className="w-full text-sm"><tbody>{facts.map((f) => (<tr key={f.id} className="border-b border-slate-100 last:border-0"><td className="py-2 font-medium text-slate-700">{f.label}</td><td className="py-2 text-right"><ValueCell value={f.value} isTotal onDrill={openDrill} /></td></tr>))}</tbody></table>
        ) : <div className="py-6 text-center text-sm text-slate-500" data-testid="overview-no-totals">Balance-sheet totals aren't available to summarise here — open the Balance Sheet tab for the full governed statement.</div>}
      </Panel>
    </div>
  );
}

/* ---------------- unified Company Financial Workspace ---------------- */
export function FinancialWorkspace() {
  const { report, basis, setBasis, year, setYear, years, companyLabel, companyReports } = useSelection();
  const reportId = report?.report_id;
  const [tab, setTab] = useState("overview");
  const [mode, setMode] = useState("single"); // single | fiveyear
  const [statements, setStatements] = useState(null);
  const [stmtErr, setStmtErr] = useState(null);
  const { openDrill, openNote, drawers } = useEvidenceDrawers();

  useEffect(() => {
    if (reportId == null) return;
    setStatements(null); setStmtErr(null);
    loadReportStatements(reportId).then((s) => setStatements(Array.isArray(s) ? s : []))
      .catch((e) => setStmtErr(e?.message || String(e)));
  }, [reportId]);

  const activeStatement = tab !== "overview" && tab !== "notes" ? matchStatement(statements, basis, tab) : null;

  return (
    <div data-testid="financial-workspace">
      <SectionHead eyebrow="Company Financial Intelligence" title="Financial Intelligence"
        caption="One coherent governed workspace — pick the company once, then move across statements, notes and years. Every figure drills to source evidence. Nothing is reconstructed or fabricated on the client." />

      {/* persistent workspace sub-header */}
      <Panel className="mb-4 sticky top-16 z-20" data-testid="workspace-header">
        <div className="flex flex-wrap items-end gap-4">
          <div><div className="text-[11px] font-bold uppercase text-slate-400">Company</div><div className="text-sm font-extrabold text-slate-900">{companyLabel}</div></div>
          <Field label="Financial Year">
            <select data-testid="ws-year" value={year || ""} onChange={(e) => setYear(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">{(years || []).map((y) => <option key={y}>{y}</option>)}</select>
          </Field>
          <Field label="Basis">
            <div className="flex rounded-lg border border-slate-200 p-0.5" data-testid="ws-basis">{["STANDALONE", "CONSOLIDATED"].map((b) => (<button key={b} data-testid={`ws-basis-${b}`} onClick={() => setBasis(b)} className={`rounded-md px-3 py-1.5 text-sm font-semibold ${basis === b ? "bg-indigo-600 text-white" : "text-slate-600"}`}>{b === "STANDALONE" ? "Standalone" : "Consolidated"}</button>))}</div>
          </Field>
          <Field label="View">
            <div className="flex rounded-lg border border-slate-200 p-0.5" data-testid="ws-mode">{[["single", "Single report"], ["fiveyear", "5-Year"]].map(([m, l]) => (<button key={m} data-testid={`ws-mode-${m}`} onClick={() => setMode(m)} className={`rounded-md px-3 py-1.5 text-sm font-semibold ${mode === m ? "bg-indigo-600 text-white" : "text-slate-600"}`}>{l}</button>))}</div>
          </Field>
          <div className="ml-auto">
            {report?.official_source_url && <a data-testid="ws-source" href={report.official_source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-indigo-600 hover:border-indigo-300">Annual report <ExternalLink className="h-3.5 w-3.5" /></a>}
          </div>
        </div>
      </Panel>

      {/* tabs */}
      <div className="mb-4 flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-white p-1" data-testid="workspace-tabs">
        {STATEMENT_TABS.map((t) => (
          <button key={t.key} data-testid={`ws-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`rounded-lg px-3 py-2 text-sm font-semibold ${tab === t.key ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{t.label}</button>
        ))}
      </div>

      {stmtErr ? <GovernedUnavailable error={stmtErr} what="The statement list" /> :
       !statements ? <Loading label="Loading governed statements…" /> :
       tab === "overview" ? <WorkspaceOverview report={report} statements={statements} basis={basis} setTab={setTab} openDrill={openDrill} /> :
       tab === "notes" ? <NotesPanel /> :
       mode === "fiveyear" ? <FiveYearView companyReports={companyReports} tabKey={tab} basis={basis} openDrill={openDrill} /> :
       activeStatement ? <GovernedStatementView statementId={activeStatement.statement_id} basis={basis} openDrill={openDrill} openNote={openNote} /> : (
        <Panel className="flex flex-col items-center justify-center py-16 text-center" data-testid="ws-no-data">
          <FileBarChart2 className="mb-3 h-10 w-10 text-slate-300" />
          <div className="text-lg font-extrabold text-slate-700">Not present in governed contract</div>
          <p className="mt-2 max-w-md text-sm text-slate-500">The governed Common API supplies no <b>{STATEMENT_TABS.find((t) => t.key === tab)?.label}</b> ({basis}) statement for report {reportId}. No values are fabricated — try the other basis or a different year.</p>
        </Panel>
      )}
      {drawers}
    </div>
  );
}

/* Backward-compat wrapper for the legacy ARI module route (renders the workspace). */
export function FinancialStatementsPage() { return <FinancialWorkspace />; }

/* ---------------- Notes panel (governed source pages) ---------------- */
function NotesPanel() {
  return (
    <div className="space-y-3">
      <Panel className="flex items-start gap-3 border-indigo-100 bg-indigo-50/40" data-testid="notes-panel-intro">
        <FileText className="mt-0.5 h-5 w-5 text-indigo-600" />
        <div className="text-sm text-slate-700">Notes &amp; schedules are governed and reached contextually: open any statement line item carrying a <b>note reference</b> to view its governed note, or browse the governed source pages below.</div>
      </Panel>
      <FullReportExplorerPage embedded />
    </div>
  );
}

/* ---------------- Full Report Explorer (governed single-page viewer) ---------------- */
export function FullReportExplorerPage({ embedded }) {
  const { report, companyLabel } = useSelection();
  const reportId = report?.report_id;
  const [docId, setDocId] = useState(null);
  const [seedErr, setSeedErr] = useState(null);
  const [page, setPage] = useState(0);
  const [content, setContent] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (reportId == null) return;
    setDocId(null); setSeedErr(null); setContent(null);
    (async () => {
      try {
        const stmts = await loadReportStatements(reportId);
        if (!stmts?.length) throw new Error("No statements to seed document id");
        const g = groupStatement(await loadStatement(stmts[0].statement_id));
        const firstVal = g.valueByCell.values().next().value;
        if (!firstVal) throw new Error("No governed value to resolve document provenance");
        const prov = await loadValueProvenance(firstVal.value_id);
        const d = prov?.value?.document_id;
        const start = prov?.value?.zero_based_page_index;
        if (d == null) throw new Error("Governed provenance did not resolve a document id");
        setDocId(d);
        setPage(Number.isFinite(Number(start)) ? Number(start) : 0);
      } catch (e) { setSeedErr(e?.message || String(e)); }
    })();
  }, [reportId]);

  useEffect(() => {
    if (docId == null || reportId == null) return;
    setLoading(true); setErr(null);
    loadDocumentPage(reportId, docId, page).then((c) => setContent(Array.isArray(c) ? c : []))
      .catch((e) => setErr(e?.message || String(e))).finally(() => setLoading(false));
  }, [docId, reportId, page]);

  return (
    <div>
      {!embedded && <SectionHead eyebrow="Report" title="Notes & Disclosures"
        caption="Governed source-page content served by the Common API document/page route. Content blocks, reading order and bounding boxes are rendered exactly as governed." />}

      <Panel className="mb-4 flex items-start gap-3 border-amber-200 bg-amber-50/50" data-testid="explorer-route-gap">
        <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-600" />
        <div className="text-sm text-amber-800"><b>Governed page navigation only.</b> The Common API exposes a single-page content route; a full page-index and cross-page search route are not yet governed, so paging is by page number and search is disabled until those routes are exposed. No content is fabricated.</div>
      </Panel>

      {seedErr ? <GovernedUnavailable error={seedErr} what="The report document" /> :
       docId == null ? <Loading label="Resolving governed document…" /> : (
        <Panel data-testid="explorer-content">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <button data-testid="explorer-prev" disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 disabled:opacity-40 hover:border-indigo-300">‹ Prev</button>
              <div className="flex items-center gap-1 text-sm">Page<input data-testid="explorer-page-input" type="number" min={0} value={page} onChange={(e) => setPage(Math.max(0, Number(e.target.value) || 0))} className="w-20 rounded-lg border border-slate-200 px-2 py-1 text-sm" /></div>
              <button data-testid="explorer-next" onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:border-indigo-300">Next ›</button>
            </div>
            <div className="text-xs text-slate-500">{companyLabel} · report {reportId} · document {docId}</div>
          </div>
          {err ? <GovernedUnavailable error={err} what="This page" /> :
           loading || content == null ? <Loading label="Loading governed page…" /> :
           content.length === 0 ? (<div className="py-12 text-center text-sm text-slate-400" data-testid="explorer-empty">No governed content on this page index (end of document).</div>) : (
            <div className="overflow-auto rounded-lg border border-slate-200" style={{ maxHeight: 620 }}>
              <div className="border-b border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-500">pdf page <b>{content[0]?.pdf_page_number}</b> · printed <b>{content[0]?.printed_page_label}</b> · quality {content[0]?.text_quality}</div>
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50"><tr><th className="px-3 py-2 text-left font-bold text-slate-700">Governed content block</th><th className="px-3 py-2 text-left font-bold text-slate-700">Type</th><th className="px-3 py-2 text-left font-bold text-slate-700">Order</th><th className="px-3 py-2 text-left font-bold text-slate-700">bbox</th></tr></thead>
                <tbody>
                  {content.map((b, i) => (
                    <tr key={`${b.content_block_id}-${i}`} data-testid="explorer-block" className="border-b border-slate-100 align-top">
                      <td className="whitespace-pre-wrap px-3 py-2 text-slate-800">{b.raw_content || b.native_text}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{b.block_type}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{b.reading_order}</td>
                      <td className="px-3 py-2 font-mono text-[10px] text-slate-400">{b.x0},{b.y0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}

/* Notes & Disclosures top-level destination (wraps the governed explorer). */
export function NotesDisclosuresPage() {
  return (
    <div>
      <SectionHead eyebrow="Reports" title="Notes & Disclosures"
        caption="Governed notes, schedules and annual-report source pages. Notes are also reachable contextually from any statement line item that carries a note reference." />
      <FullReportExplorerPage embedded />
    </div>
  );
}
